import { EMatchingPaymentTicketStatus } from '../../../../types/enums'

/**
 * Разбивка матчей выплаты по исходу депозита — для колонки «Кол-во матчей».
 *
 * Корзины намеренно не три: просрочка и отмена — разные события (истёк таймаут
 * vs решение), и на проде просрочка кратно массовее, поэтому держим её
 * нейтральным цветом, а красный оставляем за реальным отказом.
 */
export type MatchBucket =
  | 'success'
  | 'inProgress'
  | 'disputed'
  | 'released'
  | 'expired'
  | 'declined'

export type MatchCountBreakdown = Record<MatchBucket, number>

export interface MatchBreakdownBadge {
  bucket: MatchBucket
  label: string
  className: string
  count: number
}

interface DepositLike {
  status?: EMatchingPaymentTicketStatus | string | null
  // CPAYMENT-1801: статус последнего диспута по депозиту (проекция бэкенда).
  disputeStatus?: string | null
  // Выдача депозиту по этой выплате отменена (консьерж): выплата его больше не ждёт.
  matchReleased?: boolean | null
}

const BUCKET_VIEW: ReadonlyArray<Omit<MatchBreakdownBadge, 'count'>> = [
  { bucket: 'success', label: 'успех', className: 'badge-light-success' },
  { bucket: 'inProgress', label: 'в процессе', className: 'badge-light-warning' },
  { bucket: 'disputed', label: 'в споре', className: 'badge-light-info' },
  { bucket: 'released', label: 'выдача отменена', className: 'badge-light text-gray-700' },
  { bucket: 'expired', label: 'просрочено', className: 'badge-light-secondary' },
  { bucket: 'declined', label: 'отменено', className: 'badge-light-danger' },
]

const bucketOfStatus = (status: DepositLike['status']): MatchBucket => {
  switch (status) {
    case EMatchingPaymentTicketStatus.COMPLETED:
      return 'success'
    case EMatchingPaymentTicketStatus.EXPIRED:
      return 'expired'
    case EMatchingPaymentTicketStatus.DECLINED:
      return 'declined'
    default:
      // Всё остальное депозит ещё может доиграть до успеха — включая транзитный
      // NOT_CONFIRMED, который бэкенд не считает занятым слотом. Расхождение
      // намеренное: слот и исход — разные вопросы.
      return 'inProgress'
  }
}

// CPAYMENT-1801: депозит с открытым спором заморожен и слот выплаты НЕ держит — выплата всё это
// время принимает новые пополнения. Отдельная корзина, потому что «в процессе» объясняло бы
// занятость, которой нет; а после успеха спора депозит возвращается в ёмкость, и занятых слотов
// может стать больше лимита. Как и с NOT_CONFIRMED выше, расхождение «слот vs исход» намеренное.
// Депозит с отменённой выдачей мог потом завершиться в другом месте (касса, другая выплата) —
// его статус говорит о депозите, а не об этой выплате, поэтому корзина проверяется первой.
const bucketOfDeposit = (deposit: DepositLike): MatchBucket => {
  if (deposit?.matchReleased) {
    return 'released'
  }

  return deposit?.disputeStatus === 'OPEN' ? 'disputed' : bucketOfStatus(deposit?.status)
}

export const getMatchCountBreakdown = (
  deposits: DepositLike[] | null | undefined
): MatchCountBreakdown => {
  const breakdown: MatchCountBreakdown = {
    success: 0,
    inProgress: 0,
    disputed: 0,
    released: 0,
    expired: 0,
    declined: 0,
  }

  for (const deposit of deposits ?? []) {
    breakdown[bucketOfDeposit(deposit)] += 1
  }

  return breakdown
}

/** Только непустые корзины — нулевой бейдж не рисуем, чтобы не шуметь в таблице. */
export const getMatchBreakdownBadges = (
  deposits: DepositLike[] | null | undefined
): MatchBreakdownBadge[] => {
  const breakdown = getMatchCountBreakdown(deposits)

  return BUCKET_VIEW.filter((view) => breakdown[view.bucket] > 0).map((view) => ({
    ...view,
    count: breakdown[view.bucket],
  }))
}

/**
 * «Занято слотов из лимита». Оба числа приходят с бэкенда: занятость считается
 * предикатом живого депозита движка матчинга, лимит — MAX_N_OF_CHUNKS из
 * снапшота конфига выплаты. Нет снапшота → лимит неизвестен, показываем только
 * занятые, а не выдуманное «из N».
 *
 * Занятых может оказаться БОЛЬШЕ лимита: депозит со спором слот не держит, и за время спора его
 * могли забрать другие, а успех спора возвращает депозит в выплату сверх лимита (CPAYMENT-1801).
 * Это штатный исход, поэтому число не подрезаем — превышение и есть сигнал оператору.
 */
export const formatMatchSlots = (
  liveMatchCount: number | null | undefined,
  maxMatchCount: number | null | undefined
): string => {
  const live = Number(liveMatchCount ?? 0)
  const max = maxMatchCount === null || maxMatchCount === undefined ? null : Number(maxMatchCount)

  return max === null || !Number.isFinite(max) ? `${live}` : `${live} из ${max}`
}
